import { resilientFanOutQueryOptions } from 'constants/'
import { useCustomer, useLatestOpportunity } from 'hooks'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { DocumentType } from 'types'
import { logFactory } from 'utils/logFactory'
import { useDocumentSigningService, useDocumentsService } from '../services'

const hookName = 'useCustomerDocumentSignings'
const log = logFactory(hookName)

const useCustomerDocumentSignings = (options: { enabled?: boolean } = {}) => {
  const { data: customer } = useCustomer()

  const { data: latestOpportunity } = useLatestOpportunity(customer?.salesforceId as string, {
    enabled: Boolean(customer?.salesforceId),
  })

  const customerGid = latestOpportunity?.customerGid || customer?.gid || ''
  const opportunityGid = latestOpportunity?.gid || ''
  const country = latestOpportunity?.country || customer?.countryCode || ''
  const isEnabled = Boolean(customerGid && country && opportunityGid && options?.enabled !== false)

  const { getSigningDefinitions, getSigningRequests } = useDocumentSigningService()
  const { getRequiredDocuments } = useDocumentsService()

  const fetchStatus = async () => {
    const requiredDocumentsRequest = { country, customerGid, opportunityGid }
    const signingDefinitionsRequest = { country }

    log(`📓 Fetching required documents...`, requiredDocumentsRequest)
    log(`✍️ Fetching signing definitions...`, signingDefinitionsRequest)

    const [requiredDocuments, signingDefinitions] = await Promise.all([
      getRequiredDocuments(requiredDocumentsRequest),
      getSigningDefinitions(signingDefinitionsRequest),
    ])

    log(`📓 Found ${requiredDocuments.length} required documents`, requiredDocuments)
    log(`✍️ Found ${signingDefinitions.length} signing definitions`, signingDefinitions)

    const documentTypeToSigningDefinitionGids = signingDefinitions.reduce((acc, signingDefinition) => {
      acc[signingDefinition.documentType] = [...(acc[signingDefinition.documentType] ?? []), signingDefinition.gid]

      return acc
    }, {} as Record<DocumentType, string[]>)

    const fetchSignings = requiredDocuments
      .filter(({ documentType, documentGid, mustBeSigned }) => {
        // This is necessary because the document service doesn't support `mustBeSigned` query parameter yet
        if (!mustBeSigned || !documentGid || !documentType) {
          return false
        }

        const signingDefinitionGids = documentTypeToSigningDefinitionGids[documentType] ?? []

        if (signingDefinitionGids.length === 0) {
          return false
        }

        return true
      })
      .map(async (document) => {
        const { documentGid, documentType } = document
        const signingDefinitionGids = documentTypeToSigningDefinitionGids[documentType] ?? []

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const signingRequestsRequest = { documentGid: documentGid!, signingDefinitionGid: signingDefinitionGids }

        log(
          `🔍 Fetching signing requests for document ${documentGid} with signing definitions ${signingDefinitionGids.join(
            ', '
          )}`,
          signingRequestsRequest
        )

        const requests = (await getSigningRequests(signingRequestsRequest)) ?? []

        log(`🔍 Found ${requests.length} signing requests`, requests)

        const isCompleted = requests.some(({ status }) => status === 'COMPLETED')

        return {
          isCompleted,
          requiredDocument: document,
          signingRequests: requests,
        }
      })

    const signings = await Promise.all(fetchSignings)

    log(`🔍 Found ${signings.length} customer document signings`, signings)

    return signings
  }

  const queryKey = useMemo(() => [hookName, { country, customerGid }], [country, customerGid])

  return useQuery(queryKey, fetchStatus, {
    ...resilientFanOutQueryOptions,
    enabled: isEnabled,
    refetchOnMount: false,
  })
}

export { useCustomerDocumentSignings }
